import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ArrowCounterClockwise, FileCsv, Sparkle, Warning } from '@phosphor-icons/react';

import {
  matchColumns,
  type ImportSkipped,
  type ImportValidationError,
  type MemberImportField,
} from '../../api/members';
import { LogoLoader } from '../../components/LogoLoader';
import { clientLog } from '../../lib/clientLog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FIELDS,
  SHEET_KEY,
  applyLayout,
  applySheetTeamDefault,
  canImport,
  defaultLayouts,
  extractRawSheets,
  previewSheets,
  suggestionsToMapping,
  type FieldMapping,
} from '../../lib/memberImport';
import type { OnboardingDraft, OnboardingStepProps } from '../types';

// Step 3 of onboarding. UX:
//   - No file picked yet: show body text + "Choose file" button. Skipping by
//     clicking the wizard's Slutför is allowed and supported.
//   - File chosen: parse + run AI column matching with a visible spinner.
//   - Parsed: show field-driven mapping table, with a "Change file" link at
//     the top so the user can reset if they grabbed the wrong file. The
//     wizard's Slutför is the only way forward — its onSubmit handler
//     (in Onboarding.tsx) runs translate-values + transform + createOrg +
//     bulkImport in one go.
//
// Parsed file + mapping live on the OnboardingDraft so the parent can finish
// the import without callbacks back into this component.

export function Import({ draft, update }: OnboardingStepProps) {
  const auth = useAuth();
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const hasFile = draft.importParsedRows.length > 0 && draft.importParsedHeaders.length > 0;

  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErrorCode(null);
    try {
      const rawSheets = await extractRawSheets(file);
      if (rawSheets.length === 0 || rawSheets.every((s) => s.rows.length === 0)) {
        setErrorCode('empty_file');
        return;
      }
      // Single round-trip: AI gets a preview (first ~20 rows per sheet) and
      // returns header-row indices + interleaved-separator prefixes + column
      // mappings together. applyLayout then builds the keyed ParsedFile
      // from the FULL matrix that's still here on the client.
      const result = await matchColumns(
        auth.user?.access_token ?? null,
        previewSheets(rawSheets),
      );
      const layouts = result.layouts.length > 0 ? result.layouts : defaultLayouts(rawSheets);
      const out = applyLayout(rawSheets, layouts);
      if (out.headers.length === 0 || out.rows.length === 0) {
        setErrorCode('empty_file');
        return;
      }
      update({
        importFileName: file.name,
        importParsedRows: out.rows,
        importParsedHeaders: out.headers,
        importParsedSheetNames: out.sheetNames,
        importMapping: applySheetTeamDefault(
          suggestionsToMapping(result.suggestions),
          out.sheetNames,
        ),
        importSuggestionSource: result.source,
        importValidationErrors: [],
        importWouldSkip: [],
      });
    } catch (err) {
      clientLog.error('onboarding: import file parse / AI mapping failed', {
        error: err,
        fields: {
          step: 'onboarding/import',
          stage: 'parse_or_match',
          fileName: file.name,
          fileSize: file.size,
        },
      });
      setErrorCode('parse_error');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setErrorCode(null);
    update({
      importFileName: '',
      importParsedRows: [],
      importParsedHeaders: [],
      importParsedSheetNames: [],
      importMapping: {},
      importSuggestionSource: null,
      importValidationErrors: [],
      importWouldSkip: [],
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div
        role="status"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          padding: '2rem 1rem',
        }}
      >
        <LogoLoader label={t('onboarding.import.aiMapping')} size={220} />
        <p style={{ color: 'var(--color-fg-muted)', margin: 0 }}>
          {t('onboarding.import.aiMapping')}
        </p>
      </div>
    );
  }

  if (hasFile) {
    return (
      <MappingTable
        headers={draft.importParsedHeaders}
        rows={draft.importParsedRows}
        sheetNames={draft.importParsedSheetNames}
        mapping={draft.importMapping}
        source={draft.importSuggestionSource}
        fileName={draft.importFileName}
        validationErrors={draft.importValidationErrors}
        wouldSkip={draft.importWouldSkip}
        onChange={(field, column) => {
          const next = { ...draft.importMapping };
          if (column === '') delete next[field];
          else next[field] = column;
          // Clear preflight feedback on every mapping change — the answers
          // computed against the previous mapping aren't valid anymore. The
          // wizard re-runs validateImport on the next Slutför click.
          update({
            importMapping: next,
            importValidationErrors: [],
            importWouldSkip: [],
          });
        }}
        onChangeFile={() => {
          reset();
          // Re-open the picker so the user only clicks once.
          setTimeout(() => fileInputRef.current?.click(), 0);
        }}
      />
    );
  }

  return (
    <div className="form">
      <p>{t('onboarding.import.body')}</p>

      <button
        type="button"
        className="btn btn--primary"
        onClick={() => fileInputRef.current?.click()}
        style={{ alignSelf: 'flex-start' }}
      >
        <FileCsv size={16} aria-hidden />
        {t('onboarding.import.pickFile')}
      </button>

      <p className="form__hint">{t('onboarding.import.formats')}</p>
      <p className="form__hint">{t('onboarding.import.skipHint')}</p>

      {errorCode && (
        <p role="alert" className="error">
          {t(`onboarding.import.error.${errorCode}`, {
            defaultValue: t('onboarding.import.error.unknown'),
          })}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        onChange={onFilePicked}
        style={{ display: 'none' }}
      />
    </div>
  );
}

// MappingTable — field-driven layout. One row per canonical destination
// field; the dropdown picks which source column feeds it. Combined-name
// columns (when first_name and last_name share a source column) get a
// preview that shows the SPLIT portion for each axis, not the raw cell.
function MappingTable({
  headers,
  rows,
  sheetNames,
  mapping,
  source,
  fileName,
  validationErrors,
  wouldSkip,
  onChange,
  onChangeFile,
}: {
  headers: string[];
  rows: Record<string, string>[];
  sheetNames: string[];
  mapping: FieldMapping;
  source: 'ai' | 'fuzzy' | null;
  fileName: string;
  validationErrors: ImportValidationError[];
  wouldSkip: ImportSkipped[];
  onChange: (field: MemberImportField, column: string) => void;
  onChangeFile: () => void;
}) {
  const { t } = useTranslation('common');

  // First non-empty value per source column, used for the preview cell.
  const firstSampleByColumn = useMemo(() => {
    const out: Record<string, string> = {};
    for (const h of headers) {
      let sample = '';
      for (const r of rows) {
        const v = (r[h] ?? '').trim();
        if (v) {
          sample = v;
          break;
        }
      }
      out[h] = sample || '—';
    }
    return out;
  }, [headers, rows]);

  const splitsCombinedName =
    !!mapping.first_name && mapping.first_name === mapping.last_name;

  // sheetsAsTeams is now a confirmation state — true when the user has
  // explicitly opted in to "use sheet names" via the dropdown (set as the
  // default for multi-sheet files at parse time).
  const sheetsAsTeams =
    mapping.team === SHEET_KEY && sheetNames.length > 1;
  const multiSheetFile = sheetNames.length > 1;

  const ready = canImport(mapping);

  // Source columns the user has not assigned to any canonical field.
  const ignoredColumns = useMemo(() => {
    const used = new Set<string>();
    for (const v of Object.values(mapping)) {
      if (v) used.add(v);
    }
    return headers.filter((h) => !used.has(h));
  }, [headers, mapping]);

  const previewFor = (field: MemberImportField, picked: string): string => {
    if (!picked) return '—';
    if (picked === SHEET_KEY) {
      return sheetNames[0] ?? '—';
    }
    const raw = firstSampleByColumn[picked] ?? '—';
    if (raw === '—') return raw;
    if (splitsCombinedName && (field === 'first_name' || field === 'last_name')) {
      const idx = raw.lastIndexOf(' ');
      if (idx > 0) {
        return field === 'first_name' ? raw.slice(0, idx) : raw.slice(idx + 1);
      }
    }
    return raw;
  };

  return (
    <div className="form" style={{ gap: '1rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <p style={{ margin: 0 }}>
          {t('onboarding.import.fileSummary', {
            count: rows.length,
            fileName,
          })}
        </p>
        <button type="button" className="btn btn--ghost" onClick={onChangeFile}>
          <ArrowCounterClockwise size={16} aria-hidden />
          {t('onboarding.import.staged.changeFile')}
        </button>
      </div>

      {source === 'ai' && (
        <p
          className="form__hint"
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <Sparkle size={16} aria-hidden weight="fill" style={{ flexShrink: 0 }} />
          <span>{t('members.import.aiAssisted')}</span>
        </p>
      )}

      {sheetsAsTeams && (
        <p
          className="form__hint"
          style={{
            color: 'var(--color-fg)',
            background: 'var(--color-accent-soft)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('members.import.sheetsAsTeams', {
            count: sheetNames.length,
            sheets: sheetNames.join(', '),
          })}
        </p>
      )}

      {!ready && (
        <p
          role="alert"
          className="error"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Warning size={16} aria-hidden style={{ flexShrink: 0 }} />
          <span>{t('members.import.error.required_fields_missing')}</span>
        </p>
      )}

      {splitsCombinedName && (
        <p
          className="form__hint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: 'var(--color-fg)',
          }}
        >
          <Sparkle size={14} aria-hidden weight="fill" style={{ flexShrink: 0 }} />
          <span>{t('members.import.splitsCombined', { column: mapping.first_name })}</span>
        </p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.5rem', width: '12rem' }}>
                {t('members.import.column.field')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('members.import.column.sourceColumn')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>
                {t('members.import.column.preview')}
              </th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f) => {
              const picked = mapping[f.value] ?? '';
              return (
                <tr key={f.value}>
                  <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                    {t(`members.import.field.${f.value}`)}
                    {f.required && (
                      <span aria-hidden style={{ color: 'var(--color-error)', marginLeft: '0.25rem' }}>
                        {t('members.import.requiredMarker')}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    <Select
                      value={picked === '' ? '__ignore__' : picked}
                      onValueChange={(v) =>
                        onChange(f.value, v === '__ignore__' ? '' : v)
                      }
                    >
                      <SelectTrigger
                        aria-label={t(`members.import.field.${f.value}`)}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__ignore__">
                          {t('members.import.field.ignore')}
                        </SelectItem>
                        {f.value === 'team' && multiSheetFile && (
                          <SelectItem value={SHEET_KEY}>
                            {t('members.import.field.useSheetNames', {
                              count: sheetNames.length,
                            })}
                          </SelectItem>
                        )}
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td style={{ padding: '0.5rem', color: 'var(--color-fg-muted)' }}>
                    {previewFor(f.value, picked)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {ignoredColumns.length > 0 && (
        <p className="form__hint">
          {t('members.import.ignoredColumns', {
            count: ignoredColumns.length,
            columns: ignoredColumns.join(', '),
          })}
        </p>
      )}

      {validationErrors.length > 0 && <ValidationErrors errors={validationErrors} />}
      {wouldSkip.length > 0 && <WouldSkipNotices skipped={wouldSkip} />}
    </div>
  );
}

// ValidationErrors renders the blocking preflight findings — typically rows
// with a missing first_name or last_name. Same visual treatment as the
// in-app ImportMembersModal so the user sees a consistent error surface.
function ValidationErrors({ errors }: { errors: ImportValidationError[] }) {
  const { t } = useTranslation('common');
  const byRow = useMemo(() => {
    const out = new Map<number, ImportValidationError[]>();
    for (const e of errors) {
      const list = out.get(e.row_index) ?? [];
      list.push(e);
      out.set(e.row_index, list);
    }
    return out;
  }, [errors]);

  return (
    <div role="alert" className="error" style={{ marginTop: '0.5rem' }}>
      <p
        style={{
          marginBottom: '0.5rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
        }}
      >
        <Warning size={16} aria-hidden />
        {t('members.import.error.validation_failed', { count: errors.length })}
      </p>
      <ul style={{ marginLeft: '1rem', listStyle: 'disc' }}>
        {Array.from(byRow.entries())
          .slice(0, 8)
          .map(([rowIdx, errs]) => {
            const detail = errs
              .map((e) =>
                t(`members.import.error.reason.${e.reason}`, {
                  field: t(`members.import.field.${e.field}`, { defaultValue: e.field }),
                  defaultValue: `${e.field} ${e.reason}`,
                }),
              )
              .join(', ');
            return (
              <li key={rowIdx}>
                {t('members.import.error.rowDetail', { row: rowIdx + 1, detail })}
              </li>
            );
          })}
        {byRow.size > 8 && (
          <li>{t('members.import.error.more_rows', { count: byRow.size - 8 })}</li>
        )}
      </ul>
    </div>
  );
}

// WouldSkipNotices renders the informational preflight findings — rows whose
// role values couldn't be canonicalised. These do NOT block the import; we
// just want the user to know what's about to happen so an unexpected drop
// doesn't surface only after the org is created.
function WouldSkipNotices({ skipped }: { skipped: ImportSkipped[] }) {
  const { t } = useTranslation('common');
  return (
    <div
      className="form__hint"
      style={{
        background: 'var(--color-accent-soft)',
        color: 'var(--color-fg)',
        padding: '0.5rem 0.75rem',
        borderRadius: 'var(--radius-md)',
        marginTop: '0.5rem',
      }}
    >
      <p style={{ marginBottom: '0.4rem', fontWeight: 600 }}>
        {t('members.import.skipReason.intro', { count: skipped.length })}
      </p>
      <ul style={{ marginLeft: '1rem', listStyle: 'disc' }}>
        {skipped.slice(0, 8).map((s, i) => {
          const key =
            s.reason === 'merged_with_existing' && !s.value
              ? 'merged_with_existing_no_value'
              : s.reason;
          return (
            <li key={i} style={{ fontSize: '0.875rem' }}>
              {t(`members.import.skipReason.${key}`, {
                row: s.row_index + 1,
                value: s.value ?? '',
                defaultValue: t('members.import.skipReason.unknown_member_role', {
                  row: s.row_index + 1,
                  value: s.reason,
                }),
              })}
            </li>
          );
        })}
        {skipped.length > 8 && (
          <li>{t('members.import.error.more_rows', { count: skipped.length - 8 })}</li>
        )}
      </ul>
    </div>
  );
}

// importIsValid gates the wizard's Slutför button:
//   - No file picked → true (skipping is fine)
//   - File picked but mapping doesn't cover first_name + last_name → false
//     (the user must finish or reset before proceeding)
//   - File picked, mapping valid → true
export const importIsValid = (draft: OnboardingDraft): boolean => {
  if (draft.importParsedRows.length === 0) return true;
  return canImport(draft.importMapping);
};
